import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthProvider, AuthCallback, ProtectedRoute } from "@/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import CampaignForm from "@/pages/CampaignForm";
import CampaignDetail from "@/pages/CampaignDetail";
import RecordPage from "@/pages/RecordPage";
import ThankYou from "@/pages/ThankYou";
import Widget from "@/pages/Widget";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import { Privacy, Terms } from "@/pages/Legal";
import Integrations from "@/pages/Integrations";
import Admin from "@/pages/Admin";
import Billing from "@/pages/Billing";
import {
  resolveLocale, setCookieLocale, getLocaleFromPath,
  stripLocaleFromPath, DEFAULT_LOCALE, SUPPORTED, withLocale,
} from "@/lib/locale";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

/**
 * Localized layout: reads /:locale from the URL and syncs i18n + cookie.
 * Guards against invalid locales.
 */
function LocalizedRoutes() {
  const { locale } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!SUPPORTED.includes(locale)) return;
    if (i18n.language?.slice(0, 2) !== locale) i18n.changeLanguage(locale);
    setCookieLocale(locale);
  }, [locale, i18n]);

  if (!SUPPORTED.includes(locale)) {
    return <Navigate to={`/${DEFAULT_LOCALE}`} replace />;
  }

  return (
    <Routes>
      <Route index element={<Landing />} />
      <Route path="login" element={<Login />} />
      <Route path="pricing" element={<Pricing />} />
      <Route path="integrations" element={<Integrations />} />
      <Route path="privacidade" element={<Privacy />} />
      <Route path="privacy" element={<Privacy />} />
      <Route path="termos" element={<Terms />} />
      <Route path="terms" element={<Terms />} />
      <Route path="*" element={<Navigate to={`/${locale}`} replace />} />
    </Routes>
  );
}

/**
 * Redirect any non-prefixed public URL to the same URL under the resolved locale.
 * Preserves path, query and hash — critical for legacy widgets/emails.
 */
function LegacyRedirect() {
  const location = useLocation();
  const [target, setTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { locale } = await resolveLocale({ pathname: location.pathname });
      if (cancelled) return;
      setTarget(withLocale(locale, stripLocaleFromPath(location.pathname)) + location.search + location.hash);
    })();
    return () => { cancelled = true; };
  }, [location.pathname, location.search, location.hash]);

  if (!target) return null;
  return <Navigate to={target} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Locale-prefixed public routes */}
      <Route path="/:locale/*" element={<LocalizedRoutes />} />

      {/* Authenticated / non-localized areas (follow user cookie preference) */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/campaigns/new" element={<ProtectedRoute><CampaignForm /></ProtectedRoute>} />
      <Route path="/campaigns/:id/edit" element={<ProtectedRoute><CampaignForm /></ProtectedRoute>} />
      <Route path="/campaigns/:id" element={<ProtectedRoute><CampaignDetail /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />

      {/* Campaign-owned pages: no locale prefix — the campaign owns its locale */}
      <Route path="/c/:slug" element={<RecordPage />} />
      <Route path="/c/:slug/obrigado" element={<ThankYou />} />
      <Route path="/c/:slug/thanks" element={<ThankYou />} />
      <Route path="/w/:slug" element={<Widget />} />

      {/* Legacy public URLs → redirect to /pt|/en preserving path */}
      <Route path="/" element={<LegacyRedirect />} />
      <Route path="/login" element={<LegacyRedirect />} />
      <Route path="/pricing" element={<LegacyRedirect />} />
      <Route path="/integrations" element={<LegacyRedirect />} />
      <Route path="/privacidade" element={<LegacyRedirect />} />
      <Route path="/termos" element={<LegacyRedirect />} />

      <Route path="*" element={<LegacyRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

// Helper to detect if a route is public (used by consumers who need it)
export { getLocaleFromPath };

export default App;
